'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-connect/calendar-and-contacts.mdx
 * (published at user-docs.totalaccess.co.za/total-connect/calendar-and-contacts)
 */

import UserDocsLink from '@/components/shared/UserDocsLink';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { TotalConnectAccessGate } from '@/components/TotalConnectAccessGate';
import { useCalendarStore } from '../stores';
import type { CalendarEvent } from '../types';
import { useCalendarReminders } from '@/hooks/useCalendarReminders';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  MapPin,
  Users,
  Calendar as CalendarIcon,
  CalendarCheck,
  Trash2,
  Edit2,
  Loader2,
  Video,
  Phone,
  Bell,
  Repeat,
  Link2,
  ExternalLink,
  MoreVertical,
  Check,
  AlertCircle,
  CalendarDays,
  CalendarRange,
  LayoutGrid,
  List,
  ChevronDown,
  RefreshCw,
  Mail,
  Send,
  UserPlus,
  Building2,
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  addDays,
  addWeeks,
  addMonths,
  subMonths,
  subWeeks,
  isSameMonth,
  isSameDay,
  isSameWeek,
  parseISO,
  setHours,
  setMinutes,
  getHours,
  getMinutes,
  differenceInMinutes,
  isToday,
  isThisWeek,
  isBefore,
  isAfter,
  addHours,
  eachDayOfInterval,
  eachHourOfInterval,
} from 'date-fns';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { isCapacitor } from '@/lib/platformUtils';
import CapacitorModalFilterHeader, {
  capacitorHeaderActionClass,
} from '@/components/CapacitorModalFilterHeader';
import EmailRecipientsInput from './EmailRecipientsInput';
import EmailContactPickerModal from '@/components/reports/EmailContactPickerModal';
import DateTimePicker from '@/components/shared/DateTimePicker';

interface EmailSuggestion {
  email: string;
  name?: string;
  source: 'recent' | 'contact' | 'company' | 'crm' | 'personal';
  avatar_url?: string;
  job_title?: string;
  contact_type?: string;
  company?: string;
}

interface EmailContactSearchResult {
  email: string;
  name?: string;
  source: EmailSuggestion['source'];
  avatar_url?: string;
  photo_url?: string;
  job_title?: string;
  contact_type?: string;
  company?: string;
}

type ViewType = 'month' | 'week' | 'day' | 'agenda';

type CalendarSubView = 'my-calendar' | 'shared' | 'upcoming';

interface EnhancedCalendarModuleProps {
  clientId: number;
  userId: number;
  onScheduleMeeting?: (date: Date) => void;
  showNewEvent?: boolean;
  onNewEventClose?: () => void;
  calendarSubView?: CalendarSubView;
  externalEvents?: CalendarEvent[];
  hideHeader?: boolean;
  /** Pre-populate a meeting attendee from another Total Connect surface. */
  initialAttendee?: { email: string; name?: string };
  /**
   * When true (e.g. standalone Total Connect desktop), show My / Team / Upcoming tabs inline
   * because UnifiedSideNav is not rendered.
   */
  showStandaloneSubNav?: boolean;
  onCalendarSubViewChange?: (sub: CalendarSubView) => void;
}

const CALENDAR_START_HOUR = 6;
const CALENDAR_END_HOUR = 22;
const CALENDAR_GRID_HOURS = CALENDAR_END_HOUR - CALENDAR_START_HOUR + 1;
const CALENDAR_GRID_TOTAL_MINUTES = CALENDAR_GRID_HOURS * 60;
const CALENDAR_GRID_HEIGHT_PX = CALENDAR_GRID_HOURS * 64;

// Time slots for day/week view (6am to 10pm)
const TIME_SLOTS = Array.from({ length: CALENDAR_GRID_HOURS }, (_, i) => i + CALENDAR_START_HOUR);

// Event colors for visual distinction
const EVENT_COLORS = [
  { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-300 dark:border-blue-700' },
  { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300', border: 'border-green-300 dark:border-green-700' },
  { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-700' },
  { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-300 dark:border-orange-700' },
  { bg: 'bg-pink-100 dark:bg-pink-900/40', text: 'text-pink-700 dark:text-pink-300', border: 'border-pink-300 dark:border-pink-700' },
  { bg: 'bg-cyan-100 dark:bg-cyan-900/40', text: 'text-cyan-700 dark:text-cyan-300', border: 'border-cyan-300 dark:border-cyan-700' },
];

const getEventColor = (eventId: string | number) => {
  const index = String(eventId).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % EVENT_COLORS.length;
  return EVENT_COLORS[index];
};

interface PositionedCalendarEvent {
  event: CalendarEvent;
  top: string;
  height: string;
  left: string;
  width: string;
}

function parseEventDate(date: Date | string): Date {
  return typeof date === 'string' ? parseISO(date) : new Date(date);
}

function clampCalendarMinute(minute: number): number {
  return Math.min(
    (CALENDAR_END_HOUR + 1) * 60,
    Math.max(CALENDAR_START_HOUR * 60, minute),
  );
}

export default function EnhancedCalendarModule({
  clientId,
  userId,
  onScheduleMeeting,
  showNewEvent,
  onNewEventClose,
  calendarSubView = 'my-calendar',
  externalEvents = [],
  hideHeader = false,
  initialAttendee,
  showStandaloneSubNav = false,
  onCalendarSubViewChange,
}: EnhancedCalendarModuleProps) {
  const router = useRouter();
  const {
    events,
    teams,
    selectedTeamId,
    showTeamEvents,
    selectedDate,
    view,
    loading,
    syncing,
    setSelectedDate,
    setView,
    setSelectedTeamId,
    setShowTeamEvents,
    fetchEvents,
    fetchTeams,
    createEvent,
    updateEvent,
    deleteEvent,
    syncCalDAV,
  } = useCalendarStore();

  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showMiniCalendar, setShowMiniCalendar] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const openBookingFormsAdmin = useCallback((mode: 'browse' | 'create' = 'browse') => {
    const params = new URLSearchParams({ tab: 'bookings' });
    if (mode === 'create') {
      params.set('booking_action', 'create');
    }
    router.push(`/client-zone/modules/total-forms?${params.toString()}`);
  }, [router]);

  // Calendar reminder notifications - monitors events and shows desktop notifications
  useCalendarReminders(events, {
    enabled: true,
    defaultReminderMinutes: 15,
    onEventClick: (eventId) => {
      const event = events.find(e => e.id === eventId);
      if (event) {
        setSelectedEvent(event);
        setShowEventModal(true);
      }
    }
  });

  // Apply calendar sub-view settings (my-calendar, shared, upcoming)
  useEffect(() => {
    if (calendarSubView === 'my-calendar') {
      // My Calendar - only personal events, no team events
      setSelectedTeamId(null);
      setShowTeamEvents(false);
    } else if (calendarSubView === 'shared') {
      // Shared Calendars - show all team events
      setSelectedTeamId(null);
      setShowTeamEvents(true);
    } else if (calendarSubView === 'upcoming') {
      // Upcoming Events - show all events, use agenda view
      setSelectedTeamId(null);
      setShowTeamEvents(true);
      setView('agenda');
    }
    // Fetch teams when needed
    if (calendarSubView === 'shared') {
      fetchTeams();
    }
  }, [calendarSubView, setSelectedTeamId, setShowTeamEvents, setView, fetchTeams]);

  // Handle showNewEvent prop from parent
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (showNewEvent) {
      setSelectedEvent(null);
      setIsEditing(true);  // Set to editing mode for new event creation
      setShowEventModal(true);
      onNewEventClose?.();
    }
  }, [showNewEvent, onNewEventClose]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Update current time every minute for the time indicator line
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch events when date range or team filter changes
  useEffect(() => {
    const startDate = startOfMonth(subMonths(selectedDate, 1));
    const endDate = endOfMonth(addMonths(selectedDate, 1));
    fetchEvents(startDate, endDate);
  }, [selectedDate, fetchEvents, showTeamEvents, selectedTeamId]);

  const calendarEvents = useMemo(() => {
    const mergedEvents = new Map<string, CalendarEvent>();

    for (const event of events) {
      mergedEvents.set(event.id, event);
    }

    for (const event of externalEvents) {
      if (!mergedEvents.has(event.id)) {
        mergedEvents.set(event.id, event);
      }
    }

    return Array.from(mergedEvents.values());
  }, [events, externalEvents]);

  const selectedEventIsReadOnly = selectedEvent?.syncSource === 'smarttask-appointment';

  // Get days for month view
  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(selectedDate), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(selectedDate), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [selectedDate]);

  // Get days for week view
  const weekDays = useMemo(() => {
    const start = startOfWeek(selectedDate, { weekStartsOn: 0 });
    const end = endOfWeek(selectedDate, { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [selectedDate]);

  // Get events for a specific day
  const getEventsForDay = useCallback((date: Date) => {
    return calendarEvents.filter((event) => {
      const eventDate = parseEventDate(event.start);
      return isSameDay(eventDate, date);
    }).sort((a, b) => {
      const aStart = parseEventDate(a.start);
      const bStart = parseEventDate(b.start);
      return aStart.getTime() - bStart.getTime();
    });
  }, [calendarEvents]);

  const getPositionedEventsForDay = useCallback((date: Date): PositionedCalendarEvent[] => {
    const timedEvents = getEventsForDay(date)
      .filter((event) => !event.allDay)
      .map((event) => {
        const start = parseEventDate(event.start);
        const end = parseEventDate(event.end);
        const startMinutes = getHours(start) * 60 + getMinutes(start);
        const rawEndMinutes = getHours(end) * 60 + getMinutes(end);
        const endMinutes = rawEndMinutes > startMinutes ? rawEndMinutes : startMinutes + 30;

        return {
          event,
          startMinutes,
          endMinutes,
          clampedStartMinutes: clampCalendarMinute(startMinutes),
          clampedEndMinutes: clampCalendarMinute(endMinutes),
        };
      })
      .filter((entry) => entry.clampedEndMinutes > entry.clampedStartMinutes)
      .sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);

    const clusters: typeof timedEvents[] = [];
    let currentCluster: typeof timedEvents = [];
    let currentClusterEnd = -Infinity;

    timedEvents.forEach((entry) => {
      if (currentCluster.length === 0 || entry.startMinutes < currentClusterEnd) {
        currentCluster.push(entry);
        currentClusterEnd = Math.max(currentClusterEnd, entry.endMinutes);
        return;
      }

      clusters.push(currentCluster);
      currentCluster = [entry];
      currentClusterEnd = entry.endMinutes;
    });

    if (currentCluster.length > 0) {
      clusters.push(currentCluster);
    }

    return clusters.flatMap((cluster) => {
      const columnEndMinutes: number[] = [];
      const placedEntries = cluster.map((entry) => {
        let column = columnEndMinutes.findIndex((endMinute) => endMinute <= entry.startMinutes);
        if (column === -1) {
          column = columnEndMinutes.length;
          columnEndMinutes.push(entry.endMinutes);
        } else {
          columnEndMinutes[column] = entry.endMinutes;
        }

        return {
          ...entry,
          column,
        };
      });

      const totalColumns = Math.max(
        placedEntries.length > 0 ? Math.max(...placedEntries.map((entry) => entry.column)) + 1 : 1,
        1,
      );

      return placedEntries.map((entry) => {
        const width = 100 / totalColumns;
        const top = ((entry.clampedStartMinutes - CALENDAR_START_HOUR * 60) / CALENDAR_GRID_TOTAL_MINUTES) * 100;
        const height = ((entry.clampedEndMinutes - entry.clampedStartMinutes) / CALENDAR_GRID_TOTAL_MINUTES) * 100;

        return {
          event: entry.event,
          top: `${Math.max(0, top)}%`,
          height: `${Math.max((30 / CALENDAR_GRID_HEIGHT_PX) * 100, Math.min(100 - top, height))}%`,
          left: `${entry.column * width}%`,
          width: `${width}%`,
        };
      });
    });
  }, [getEventsForDay]);

  // Navigation handlers
  const handlePrev = () => {
    switch (view) {
      case 'month':
        setSelectedDate(subMonths(selectedDate, 1));
        break;
      case 'week':
        setSelectedDate(subWeeks(selectedDate, 1));
        break;
      case 'day':
        setSelectedDate(addDays(selectedDate, -1));
        break;
    }
  };

  const handleNext = () => {
    switch (view) {
      case 'month':
        setSelectedDate(addMonths(selectedDate, 1));
        break;
      case 'week':
        setSelectedDate(addWeeks(selectedDate, 1));
        break;
      case 'day':
        setSelectedDate(addDays(selectedDate, 1));
        break;
    }
  };

  const handleToday = () => setSelectedDate(new Date());

  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
    if (view === 'month') {
      setView('day');
    } else {
      setSelectedEvent(null);
      setIsEditing(true);
      setShowEventModal(true);
    }
  };

  const handleTimeSlotClick = (date: Date, hour: number) => {
    const eventDate = setMinutes(setHours(date, hour), 0);
    setSelectedDate(eventDate);
    setSelectedEvent(null);
    setIsEditing(true);
    setShowEventModal(true);
  };

  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setIsEditing(false);
    setShowEventModal(true);
  };

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setIsEditing(true);
    setShowEventModal(true);
  };

  const handleEditEvent = () => {
    if (selectedEventIsReadOnly) {
      return;
    }

    setIsEditing(true);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent || selectedEvent.syncSource === 'smarttask-appointment') {
      return;
    }

    if (selectedEvent) {
      await deleteEvent(selectedEvent.id);
      setShowEventModal(false);
      setSelectedEvent(null);
    }
  };

  const handleCloseModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
    setIsEditing(false);
  };

  // Get the title for the header based on view
  const getHeaderTitle = () => {
    switch (view) {
      case 'month':
        return format(selectedDate, 'MMMM yyyy');
      case 'week':
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
        const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 0 });
        if (isSameMonth(weekStart, weekEnd)) {
          return `${format(weekStart, 'MMMM d')} - ${format(weekEnd, 'd, yyyy')}`;
        }
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      case 'day':
        return format(selectedDate, 'EEEE, MMMM d, yyyy');
      case 'agenda':
        return 'Upcoming Events';
      default:
        return '';
    }
  };

  // Current time indicator position
  const currentTimePosition = useMemo(() => {
    const hour = getHours(currentTime) + getMinutes(currentTime) / 60;
    return ((hour - CALENDAR_START_HOUR) / CALENDAR_GRID_HOURS) * 100;
  }, [currentTime]);

  // Upcoming events for agenda view
  const upcomingEvents = useMemo(() => {
    return calendarEvents
      .filter(event => {
        const start = typeof event.start === 'string' ? parseISO(event.start) : new Date(event.start);
        return isAfter(start, startOfDay(new Date()));
      })
      .sort((a, b) => {
        const aStart = typeof a.start === 'string' ? parseISO(a.start) : new Date(a.start);
        const bStart = typeof b.start === 'string' ? parseISO(b.start) : new Date(b.start);
        return aStart.getTime() - bStart.getTime();
      })
      .slice(0, 20);
  }, [calendarEvents]);

  // Check if we're in team calendar mode
  const isTeamCalendarView = calendarSubView === 'shared';

  return (
    <TotalConnectAccessGate featureName="Calendar">
    <div className={cn(
      "h-full flex flex-col",
      hideHeader
        ? 'bg-transparent dark:bg-transparent'
        : isTeamCalendarView 
        ? "bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20" 
        : "bg-white dark:bg-gray-900"
    )}>
      {/* Header */}
      {!hideHeader && (
      <>
        {showStandaloneSubNav && onCalendarSubViewChange ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/90">
            {(
              [
                { id: 'my-calendar' as const, label: 'My Calendar' },
                { id: 'shared' as const, label: 'Team Calendar' },
                { id: 'upcoming' as const, label: 'Upcoming' },
              ] as const
            ).map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => onCalendarSubViewChange(id)}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors sm:text-sm',
                  calendarSubView === id
                    ? 'bg-white text-blue-700 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:text-blue-300 dark:ring-gray-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
      <div className={cn(
        "flex items-center justify-between p-4 border-b",
        isTeamCalendarView
          ? "border-purple-200 dark:border-purple-800/50 bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm"
          : "border-gray-200 dark:border-gray-700"
      )}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            {isTeamCalendarView && (
              <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            )}
            <h2 className={cn(
              "text-xl font-semibold",
              isTeamCalendarView
                ? "text-purple-900 dark:text-purple-100"
                : "text-gray-900 dark:text-white"
            )}>
              {isTeamCalendarView ? 'Team Calendar' : getHeaderTitle()}
              <UserDocsLink docsPath="/total-connect/calendar-and-contacts" label="User guide: Calendar & contacts" className="ml-1 align-middle" />
            </h2>
            {isTeamCalendarView && (
              <span className="text-sm text-purple-600 dark:text-purple-400">
                — {getHeaderTitle()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handlePrev}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={handleToday}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              Today
            </button>
            <button
              onClick={handleNext}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Sync Button */}
          <button
            onClick={() => syncCalDAV()}
            disabled={syncing}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
            title="Sync with email calendar (CalDAV)"
          >
            <RefreshCw className={cn('w-4 h-4', syncing && 'animate-spin')} />
            <span className="hidden lg:inline text-sm">
              {syncing ? 'Syncing...' : 'Sync'}
            </span>
          </button>

          {/* View Selector */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            {[
              { id: 'month', label: 'Month', icon: LayoutGrid },
              { id: 'week', label: 'Week', icon: CalendarRange },
              { id: 'day', label: 'Day', icon: CalendarDays },
              { id: 'agenda', label: 'Agenda', icon: List },
            ].map((v) => (
              <button
                key={v.id}
                onClick={() => setView(v.id as ViewType)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                  view === v.id
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                )}
                title={v.label}
              >
                <v.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{v.label}</span>
              </button>
            ))}
          </div>

          {/* Bookings Button */}
          <button
            onClick={() => openBookingFormsAdmin('browse')}
            className="flex items-center gap-2 px-3 py-2 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors font-medium"
            title="Open Booking Forms admin"
          >
            <CalendarCheck className="w-4 h-4" />
            <span className="hidden lg:inline">Booking Forms</span>
          </button>

          {/* New Booking Form */}
          <button
            onClick={() => openBookingFormsAdmin('create')}
            className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg transition-colors"
            title="Create booking form in Total Forms"
          >
            <ExternalLink className="w-5 h-5" />
          </button>

          <button
            onClick={handleCreateEvent}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Event</span>
          </button>
        </div>
      </div>
      </>
      )}
      {/* Team Legend - only show in team calendar view */}
      {!hideHeader && isTeamCalendarView && teams.length > 0 && (
        <div className="px-4 py-2 border-b border-purple-200 dark:border-purple-800/50 bg-white/40 dark:bg-gray-900/40">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-xs font-medium text-purple-700 dark:text-purple-300 uppercase tracking-wide">Teams:</span>
            {teams.map((team) => (
              <div
                key={team.id}
                className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                style={{ 
                  backgroundColor: team.color ? `${team.color}20` : '#8b5cf620',
                  color: team.color || '#8b5cf6',
                  borderLeft: `3px solid ${team.color || '#8b5cf6'}`
                }}
              >
                <span>{team.icon || '👥'}</span>
                <span>{team.team_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar Content */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Loading calendar...</p>
            </div>
          </div>
        ) : view === 'month' ? (
          /* Month View */
          <div className="h-full flex flex-col p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-px mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Day Cells */}
            <div className="flex-1 grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-xl overflow-hidden">
              {monthDays.map((day, idx) => {
                const dayEvents = getEventsForDay(day);
                const isCurrentMonth = isSameMonth(day, selectedDate);
                const isCurrentDay = isToday(day);
                const isSelected = isSameDay(day, selectedDate);

                return (
                  <div
                    key={idx}
                    onClick={() => handleDayClick(day)}
                    className={cn(
                      'min-h-[100px] p-2 bg-white dark:bg-gray-900 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors relative group',
                      !isCurrentMonth && 'bg-gray-50 dark:bg-gray-950 opacity-60'
                    )}
                  >
                    <div
                      className={cn(
                        'w-7 h-7 flex items-center justify-center text-sm font-medium rounded-full mb-1 transition-all',
                        isCurrentDay && 'bg-blue-600 text-white',
                        isSelected && !isCurrentDay && 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400',
                        !isCurrentDay && !isSelected && isCurrentMonth && 'text-gray-900 dark:text-white group-hover:bg-gray-100 dark:group-hover:bg-gray-800',
                        !isCurrentMonth && 'text-gray-400 dark:text-gray-600'
                      )}
                    >
                      {format(day, 'd')}
                    </div>

                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((event) => {
                        const colors = event.teamColor 
                          ? { bg: 'bg-gray-500/20', text: 'text-gray-700 dark:text-gray-200', border: '' }
                          : getEventColor(event.id);
                        return (
                          <div
                            key={event.id}
                            onClick={(e) => handleEventClick(event, e)}
                            className={cn(
                              'px-2 py-0.5 text-xs rounded truncate cursor-pointer border-l-2 transition-all hover:shadow-sm',
                              event.teamColor ? '' : colors.bg,
                              colors.text,
                              colors.border
                            )}
                            style={event.teamColor ? { 
                              backgroundColor: `${event.teamColor}20`, 
                              borderLeftColor: event.teamColor 
                            } : undefined}
                            title={event.teamName ? `${event.title} (${event.teamName})` : event.title}
                          >
                            {event.teamName && calendarSubView === 'shared' && (
                              <span className="font-medium">[{event.teamName.substring(0, 8)}] </span>
                            )}
                            {event.title}
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 px-2 font-medium">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : view === 'week' ? (
          /* Week View */
          <div className="h-full flex flex-col">
            {/* Day Headers */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
              <div className="w-16 flex-shrink-0" /> {/* Time column spacer */}
              {weekDays.map((day, idx) => (
                <div
                  key={idx}
                  className="flex-1 text-center py-3 border-l border-gray-200 dark:border-gray-700"
                >
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    {format(day, 'EEE')}
                  </div>
                  <div
                    onClick={() => handleDayClick(day)}
                    className={cn(
                      'w-8 h-8 mx-auto mt-1 flex items-center justify-center text-sm font-semibold rounded-full cursor-pointer transition-colors',
                      isToday(day)
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                    )}
                  >
                    {format(day, 'd')}
                  </div>
                </div>
              ))}
            </div>

            {/* Time Grid */}
            <div className="flex-1 overflow-y-auto">
              <div className="flex relative" style={{ minHeight: `${CALENDAR_GRID_HEIGHT_PX}px` }}>
                {/* Time Labels */}
                <div className="w-16 flex-shrink-0">
                  {TIME_SLOTS.map((hour) => (
                    <div
                      key={hour}
                      className="h-16 border-b border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400 text-right pr-2 pt-0.5"
                    >
                      {format(setHours(new Date(), hour), 'h a')}
                    </div>
                  ))}
                </div>

                {/* Day Columns */}
                {weekDays.map((day, dayIdx) => (
                  <div
                    key={dayIdx}
                    className="flex-1 border-l border-gray-200 dark:border-gray-700 relative"
                  >
                    {/* Hour slots */}
                    {TIME_SLOTS.map((hour) => (
                      <div
                        key={hour}
                        onClick={() => handleTimeSlotClick(day, hour)}
                        className="h-16 border-b border-gray-100 dark:border-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/10 cursor-pointer transition-colors"
                      />
                    ))}

                    {/* Events */}
                    {getPositionedEventsForDay(day).map((positionedEvent) => {
                      const { event } = positionedEvent;
                      const colors = getEventColor(event.id);
                      return (
                        <div
                          key={event.id}
                          onClick={(e) => handleEventClick(event, e)}
                          className={cn(
                            'absolute px-2 py-1 rounded-lg cursor-pointer border-l-2 overflow-hidden transition-all hover:shadow-md z-10',
                            colors.bg,
                            colors.border
                          )}
                          style={{
                            top: positionedEvent.top,
                            height: positionedEvent.height,
                            minHeight: '24px',
                            left: `calc(${positionedEvent.left} + 4px)`,
                            width: `calc(${positionedEvent.width} - 8px)`,
                          }}
                        >
                          <div className={cn('text-xs font-medium truncate', colors.text)}>
                            {event.title}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {format(parseEventDate(event.start), 'h:mm a')}
                          </div>
                        </div>
                      );
                    })}

                    {/* Current time indicator */}
                    {isToday(day) && currentTimePosition >= 0 && currentTimePosition <= 100 && (
                      <div
                        className="absolute left-0 right-0 z-20 pointer-events-none"
                        style={{ top: `${currentTimePosition}%` }}
                      >
                        <div className="relative flex items-center">
                          <div className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                          <div className="flex-1 h-0.5 bg-red-500" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : view === 'day' ? (
          /* Day View */
          <div className="h-full flex flex-col">
            {/* All-day events */}
            {getEventsForDay(selectedDate).filter(e => e.allDay).length > 0 && (
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">All Day</div>
                <div className="flex flex-wrap gap-2">
                  {getEventsForDay(selectedDate).filter(e => e.allDay).map((event) => {
                    const colors = getEventColor(event.id);
                    return (
                      <div
                        key={event.id}
                        onClick={(e) => handleEventClick(event, e)}
                        className={cn(
                          'px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer transition-all hover:shadow-md',
                          colors.bg,
                          colors.text
                        )}
                      >
                        {event.title}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Time Grid */}
            <div className="flex-1 overflow-y-auto">
              <div className="flex relative" style={{ minHeight: `${CALENDAR_GRID_HEIGHT_PX}px` }}>
                {/* Time Labels */}
                <div className="w-20 flex-shrink-0">
                  {TIME_SLOTS.map((hour) => (
                    <div
                      key={hour}
                      className="h-16 border-b border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400 text-right pr-3 pt-0.5"
                    >
                      {format(setHours(new Date(), hour), 'h:mm a')}
                    </div>
                  ))}
                </div>

                {/* Events Column */}
                <div className="flex-1 border-l border-gray-200 dark:border-gray-700 relative">
                  {/* Hour slots */}
                  {TIME_SLOTS.map((hour) => (
                    <div
                      key={hour}
                      onClick={() => handleTimeSlotClick(selectedDate, hour)}
                      className="h-16 border-b border-gray-100 dark:border-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/10 cursor-pointer transition-colors"
                    />
                  ))}

                  {/* Events */}
                  {getPositionedEventsForDay(selectedDate).map((positionedEvent) => {
                    const { event } = positionedEvent;
                    const colors = getEventColor(event.id);
                    
                    return (
                      <div
                        key={event.id}
                        onClick={(e) => handleEventClick(event, e)}
                        className={cn(
                          'absolute px-3 py-2 rounded-lg cursor-pointer border-l-4 overflow-hidden transition-all hover:shadow-lg z-10',
                          colors.bg,
                          colors.border
                        )}
                        style={{ 
                          top: positionedEvent.top, 
                          height: positionedEvent.height, 
                          minHeight: '48px',
                          left: `calc(${positionedEvent.left} + 1%)`,
                          width: `calc(${positionedEvent.width} - 2%)`,
                        }}
                      >
                        <div className={cn('text-sm font-semibold truncate', colors.text)}>
                          {event.title}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          <Clock className="w-3 h-3" />
                          {format(parseEventDate(event.start), 'h:mm a')} - 
                          {format(parseEventDate(event.end), 'h:mm a')}
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            {event.location}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Current time indicator */}
                  {isToday(selectedDate) && currentTimePosition >= 0 && currentTimePosition <= 100 && (
                    <div
                      className="absolute left-0 right-0 z-20 pointer-events-none"
                      style={{ top: `${currentTimePosition}%` }}
                    >
                      <div className="relative flex items-center">
                        <div className="w-3 h-3 rounded-full bg-red-500 -ml-1.5 shadow-md" />
                        <div className="flex-1 h-0.5 bg-red-500" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Agenda View */
          <div className="flex-1 overflow-y-auto p-4">
            {upcomingEvents.length === 0 ? (
              <div className="text-center py-16">
                <CalendarIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  No Upcoming Events
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Your schedule is clear! Create a new event to get started.
                </p>
                <button
                  onClick={handleCreateEvent}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Event
                </button>
              </div>
            ) : (
              <div className="space-y-4 max-w-3xl mx-auto">
                {upcomingEvents.map((event, idx) => {
                  const start = typeof event.start === 'string' ? parseISO(event.start) : new Date(event.start);
                  const end = typeof event.end === 'string' ? parseISO(event.end) : new Date(event.end);
                  const colors = getEventColor(event.id);
                  const prevEvent = upcomingEvents[idx - 1];
                  const prevStart = prevEvent ? (typeof prevEvent.start === 'string' ? parseISO(prevEvent.start) : new Date(prevEvent.start)) : null;
                  const showDateHeader = !prevStart || !isSameDay(start, prevStart);

                  return (
                    <div key={event.id}>
                      {showDateHeader && (
                        <div className="flex items-center gap-3 mb-3 mt-6 first:mt-0">
                          <div className={cn(
                            'text-center',
                            isToday(start) && 'text-blue-600 dark:text-blue-400'
                          )}>
                            <div className="text-xs font-medium uppercase tracking-wider">
                              {isToday(start) ? 'Today' : format(start, 'EEE')}
                            </div>
                            <div className="text-2xl font-bold">
                              {format(start, 'd')}
                            </div>
                            <div className="text-xs">
                              {format(start, 'MMM')}
                            </div>
                          </div>
                          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                        </div>
                      )}
                      <div
                        onClick={(e) => handleEventClick(event, e)}
                        className={cn(
                          'flex items-start gap-4 p-4 rounded-xl cursor-pointer border-l-4 transition-all hover:shadow-md',
                          colors.bg,
                          colors.border
                        )}
                      >
                        <div className="flex-shrink-0 text-center w-16">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {event.allDay ? 'All day' : format(start, 'h:mm a')}
                          </div>
                          {!event.allDay && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {format(end, 'h:mm a')}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className={cn('font-semibold truncate', colors.text)}>
                            {event.title}
                          </h4>
                          {/* Team badge in agenda view */}
                          {event.teamName && (
                            <span
                              className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                              style={{
                                backgroundColor: event.teamColor ? `${event.teamColor}20` : '#8b5cf620',
                                color: event.teamColor || '#8b5cf6',
                              }}
                            >
                              <Users className="w-3 h-3" />
                              {event.teamName}
                            </span>
                          )}
                          {event.location && (
                            <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mt-1">
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{event.location}</span>
                            </div>
                          )}
                          {event.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
                              {event.description}
                            </p>
                          )}
                          {event.attendees && event.attendees.length > 0 && (
                            <div className="flex items-center gap-2 mt-2">
                              <Users className="w-4 h-4 text-gray-400" />
                              <div className="flex -space-x-2">
                                {event.attendees.slice(0, 4).map((attendee, i) => (
                                  <div
                                    key={i}
                                    className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs font-medium ring-2 ring-white dark:ring-gray-900"
                                    title={attendee.name || attendee.email}
                                  >
                                    {(attendee.name || attendee.email)[0].toUpperCase()}
                                  </div>
                                ))}
                                {event.attendees.length > 4 && (
                                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300 ring-2 ring-white dark:ring-gray-900">
                                    +{event.attendees.length - 4}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-white/50 dark:hover:bg-gray-800/50">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Event Modal */}
      {showEventModal && (
        <EventModal
          event={selectedEvent}
          selectedDate={selectedDate}
          isEditing={isEditing}
          readOnly={selectedEventIsReadOnly}
          onClose={handleCloseModal}
          onEdit={handleEditEvent}
          onDelete={handleDeleteEvent}
          initialAttendee={initialAttendee}
          teams={teams}
          isTeamCalendarView={isTeamCalendarView}
          onSave={async (eventData, options) => {
            if (selectedEvent) {
              await updateEvent(selectedEvent.id, eventData);
            } else {
              // Ensure we have all required fields for a new event
              if (eventData.title && eventData.start && eventData.end) {
                // If we have attendees and want to send invites, use createEventWithInvites
                if (options?.sendInvites && eventData.attendees && eventData.attendees.length > 0) {
                  const result = await useCalendarStore.getState().createEventWithInvites({
                    title: eventData.title,
                    start: eventData.start,
                    end: eventData.end,
                    allDay: eventData.allDay,
                    location: eventData.location,
                    description: eventData.description,
                    color: eventData.color,
                    isVideoMeeting: eventData.isVideoMeeting,
                    attendees: eventData.attendees,
                    teamId: eventData.team_id,
                    isTeamEvent: eventData.is_team_event,
                    meetingUrl: eventData.isVideoMeeting ? undefined : undefined, // Will be generated
                  }, {
                    sendInvites: true,
                    customMessage: options.customMessage,
                  });
                  
                  // Show invitation result notification
                  if (result.inviteResult) {
                    const { sent, failed } = result.inviteResult;
                    if (sent.length > 0) {
                      console.log(`[Calendar] Invitations sent to: ${sent.join(', ')}`);
                    }
                    if (failed.length > 0) {
                      console.warn(`[Calendar] Failed to send invitations to: ${failed.map(f => f.email).join(', ')}`);
                    }
                  }
                } else {
                  // Regular event creation without invites
                  await createEvent({
                    title: eventData.title,
                    start: eventData.start,
                    end: eventData.end,
                    allDay: eventData.allDay,
                    location: eventData.location,
                    description: eventData.description,
                    color: eventData.color,
                    isVideoMeeting: eventData.isVideoMeeting,
                    attendees: eventData.attendees,
                    team_id: eventData.team_id,
                    is_team_event: eventData.is_team_event,
                  });
                }
              }
            }
            handleCloseModal();
          }}
          onScheduleMeeting={onScheduleMeeting}
        />
      )}
    </div>
    </TotalConnectAccessGate>
  );
}

export interface EventModalProps {
  event: CalendarEvent | null;
  selectedDate: Date;
  isEditing: boolean;
  readOnly?: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSave: (data: Partial<CalendarEvent> & { team_id?: string; is_team_event?: boolean }, options?: { sendInvites?: boolean; customMessage?: string }) => Promise<void>;
  onScheduleMeeting?: (date: Date) => void;
  /** Pre-populated attendee from another Total Connect surface. */
  initialAttendee?: { email: string; name?: string };
  /** Available teams for team event selector */
  teams?: { id: string; team_name: string; color: string; icon: string }[];
  /** Whether we're in team calendar view */
  isTeamCalendarView?: boolean;
  /**
   * 'modal' (default): centered dialog. 'slideover': right slide-over panel,
   * matching the TotalConnectRTC widget chrome.
   */
  presentation?: 'modal' | 'slideover';
}

interface Attendee {
  email: string;
  name?: string;
  status?: 'needsAction' | 'accepted' | 'declined' | 'tentative';
}

// Time options for dropdowns (15 min intervals)
const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const hours = Math.floor(i / 4);
  const minutes = (i % 4) * 15;
  const value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  const label = format(setMinutes(setHours(new Date(), hours), minutes), 'h:mm a');
  return { value, label };
});

// Duration options
const DURATION_OPTIONS = [
  { value: 15, label: '15m' },
  { value: 30, label: '30m' },
  { value: 45, label: '45m' },
  { value: 60, label: '1h' },
  { value: 90, label: '1.5h' },
  { value: 120, label: '2h' },
  { value: 180, label: '3h' },
  { value: 240, label: '4h' },
];

export function EventModal({
  event,
  selectedDate,
  isEditing,
  readOnly = false,
  onClose,
  onEdit,
  onDelete,
  onSave,
  onScheduleMeeting,
  initialAttendee,
  teams,
  isTeamCalendarView,
  presentation = 'modal',
}: EventModalProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const isReadonlyEvent = readOnly && Boolean(event);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(event?.teamId || null);
  // To/Cc/Bcc state (matching MultiComposeManager pattern)
  const [toField, setToField] = useState<string>(() => {
    if (event?.attendees) {
      return event.attendees
        .map(a => a.name ? `${a.name} <${a.email}>` : a.email)
        .join(', ');
    }
    // Pre-populate from an external attendee shortcut.
    if (initialAttendee) {
      return initialAttendee.name
        ? `${initialAttendee.name} <${initialAttendee.email}>`
        : initialAttendee.email;
    }
    return '';
  });
  const [ccField, setCcField] = useState('');
  const [bccField, setBccField] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [activeField, setActiveField] = useState<'to' | 'cc' | 'bcc' | null>(null);
  const [suggestions, setSuggestions] = useState<EmailSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const toInputRef = useRef<HTMLInputElement>(null);
  const ccInputRef = useRef<HTMLInputElement>(null);
  const bccInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [showContactPicker, setShowContactPicker] = useState(false);

  // Helper: count total attendees across all fields
  const getAttendeesCount = useCallback(() => {
    const count = (field: string) => field.split(',').map(e => e.trim()).filter(e => e && e.includes('@')).length;
    return count(toField) + count(ccField) + count(bccField);
  }, [toField, ccField, bccField]);

  // Parse initial dates
  const getInitialStartDate = () => {
    if (event?.start) {
      return typeof event.start === 'string' ? parseISO(event.start) : new Date(event.start);
    }
    // Default to next hour
    const now = new Date();
    const nextHour = setMinutes(setHours(selectedDate, now.getHours() + 1), 0);
    return nextHour;
  };

  const getInitialEndDate = () => {
    if (event?.end) {
      return typeof event.end === 'string' ? parseISO(event.end) : new Date(event.end);
    }
    // Default to 1 hour after start
    return addHours(getInitialStartDate(), 1);
  };

  const [formData, setFormData] = useState({
    title: event?.title || '',
    description: event?.description || '',
    location: event?.location || 'Online',
    startDate: format(getInitialStartDate(), 'yyyy-MM-dd'),
    startTime: format(getInitialStartDate(), 'HH:mm'),
    endDate: format(getInitialEndDate(), 'yyyy-MM-dd'),
    endTime: format(getInitialEndDate(), 'HH:mm'),
    allDay: event?.allDay || false,
    isVideoMeeting: event ? (event.eventType === 'online' || !!event.meetingUrl) : true,
    sendInvites: true, // Default to sending invites when there are attendees
    customMessage: '',
  });

  // Calculate duration in minutes
  const getDuration = useCallback(() => {
    const start = new Date(`${formData.startDate}T${formData.startTime}`);
    const end = new Date(`${formData.endDate}T${formData.endTime}`);
    return differenceInMinutes(end, start);
  }, [formData.startDate, formData.startTime, formData.endDate, formData.endTime]);

  // Format duration for display
  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Update start (date+time) — keeps the existing duration, defaulting to 1 hour
  const handleStartDateTimeChange = (value: string) => {
    const [d, t] = value.split('T');
    if (!d) {
      setFormData(prev => ({ ...prev, startDate: '', startTime: '' }));
      return;
    }
    const startTime = t || formData.startTime || '00:00';
    const currentDuration = getDuration();
    const validDuration = currentDuration > 0 ? currentDuration : 60;

    const newStart = new Date(`${d}T${startTime}`);
    const newEnd = new Date(newStart.getTime() + validDuration * 60000);

    setFormData(prev => ({
      ...prev,
      startDate: d,
      startTime,
      endDate: format(newEnd, 'yyyy-MM-dd'),
      endTime: format(newEnd, 'HH:mm'),
    }));
  };

  const handleEndDateTimeChange = (value: string) => {
    const [d, t] = value.split('T');
    setFormData(prev => ({
      ...prev,
      endDate: d || '',
      endTime: t || prev.endTime,
    }));
  };

  // Load suggestions from localStorage + server API (matches MultiComposeManager)
  const loadSuggestions = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Show cached suggestions immediately
    const recentEmails = JSON.parse(localStorage.getItem('recentEmailRecipients') || '[]') as EmailSuggestion[];
    const companyUsers = JSON.parse(localStorage.getItem('companyEmailUsers') || '[]') as EmailSuggestion[];
    const cachedSuggestions = [...recentEmails, ...companyUsers];
    const filtered = cachedSuggestions.filter(
      (s) => s.email.toLowerCase().includes(query.toLowerCase()) || s.name?.toLowerCase().includes(query.toLowerCase())
    );
    const unique = filtered.filter((s, i, arr) => arr.findIndex((x) => x.email === s.email) === i);
    setSuggestions(unique.slice(0, 8));
    setShowSuggestions(unique.length > 0);
    setSelectedSuggestionIndex(-1);

    // Fetch from server for comprehensive results
    try {
      let token: string | null = null;
      if (typeof window !== 'undefined') {
        const storedTokens = localStorage.getItem('auth_tokens');
        if (storedTokens) {
          try { token = JSON.parse(storedTokens).access_token || null; } catch {}
        }
      }
      if (!token) return;

      const response = await fetch('/api/v1/email-contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ action: 'searchAllContacts', query })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.length > 0) {
          const serverResults: EmailSuggestion[] = (data.data as EmailContactSearchResult[]).map((c) => ({
            email: c.email, name: c.name,
            source: c.source as EmailSuggestion['source'],
            avatar_url: c.avatar_url || c.photo_url,
            job_title: c.job_title, contact_type: c.contact_type, company: c.company
          }));
          const all = [...unique, ...serverResults];
          const deduped = all.filter((s, i, arr) => arr.findIndex((x) => x.email.toLowerCase() === s.email.toLowerCase()) === i);
          setSuggestions(deduped.slice(0, 10));
          setShowSuggestions(deduped.length > 0);
        }
      }
    } catch (err) {
      console.log('[Calendar] Server contact search failed, using cached:', err);
    }
  }, []);

  // Select a suggestion (add to active field)
  const selectSuggestion = useCallback((suggestion: EmailSuggestion) => {
    const formatted = suggestion.name ? `${suggestion.name} <${suggestion.email}>` : suggestion.email;
    const fieldSetter = activeField === 'cc' ? setCcField : activeField === 'bcc' ? setBccField : setToField;
    const currentValue = activeField === 'cc' ? ccField : activeField === 'bcc' ? bccField : toField;

    // Parse existing emails, remove any partial input, add new
    const parts = currentValue.split(',').map(s => s.trim()).filter(s => s && s.includes('@'));
    parts.push(formatted);
    fieldSetter(parts.join(', ') + ', ');

    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);

    // Save to recent
    const recent = JSON.parse(localStorage.getItem('recentEmailRecipients') || '[]') as EmailSuggestion[];
    const existing = recent.findIndex(r => r.email === suggestion.email);
    if (existing >= 0) recent.splice(existing, 1);
    recent.unshift(suggestion);
    localStorage.setItem('recentEmailRecipients', JSON.stringify(recent.slice(0, 50)));
  }, [activeField, toField, ccField, bccField]);

  const mergeContactPickerRecipients = useCallback((recipients: Array<{ email: string; name?: string }>) => {
    if (recipients.length === 0) return;
    const formatted = recipients.map((r) => (r.name?.trim() ? `${r.name.trim()} <${r.email}>` : r.email));
    setToField((current) => {
      const trimmed = current.trim();
      const sep = formatted.join(', ');
      return trimmed ? `${trimmed}${trimmed.endsWith(',') ? ' ' : ', '}${sep}` : sep;
    });

    try {
      const recentRaw =
        typeof window !== 'undefined' ? localStorage.getItem('recentEmailRecipients') : null;
      const recent: EmailSuggestion[] = recentRaw ? JSON.parse(recentRaw) : [];
      recipients.forEach((r) => {
        const sug: EmailSuggestion = {
          email: r.email.toLowerCase(),
          name: r.name?.trim() || undefined,
          source: 'crm',
        };
        const existing = recent.findIndex((item) => item.email.toLowerCase() === sug.email);
        if (existing >= 0) recent.splice(existing, 1);
        recent.unshift(sug);
      });
      localStorage.setItem('recentEmailRecipients', JSON.stringify(recent.slice(0, 50)));
    } catch {
      /* ignore malformed localStorage */
    }
  }, []);

  // Handle Tab key navigation between fields
  const handleFieldKeyDown = useCallback((e: React.KeyboardEvent, field: 'to' | 'cc' | 'bcc') => {
    if (e.key === 'ArrowDown' && showSuggestions && suggestions.length > 0) {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.min(prev + 1, suggestions.length - 1));
      return;
    }
    if (e.key === 'ArrowUp' && showSuggestions) {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.max(prev - 1, -1));
      return;
    }
    if (e.key === 'Enter' && showSuggestions && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[selectedSuggestionIndex]);
      return;
    }
    if (e.key === 'Escape') {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    if (e.key === 'Tab' && !e.shiftKey) {
      if (field === 'to' && showCc) { e.preventDefault(); ccInputRef.current?.focus(); return; }
      if (field === 'to' && showBcc) { e.preventDefault(); bccInputRef.current?.focus(); return; }
      if (field === 'cc' && showBcc) { e.preventDefault(); bccInputRef.current?.focus(); return; }
    }
    if (e.key === 'Tab' && e.shiftKey) {
      if (field === 'bcc' && showCc) { e.preventDefault(); ccInputRef.current?.focus(); return; }
      if (field === 'bcc' && !showCc) { e.preventDefault(); toInputRef.current?.focus(); return; }
      if (field === 'cc') { e.preventDefault(); toInputRef.current?.focus(); return; }
    }
  }, [showSuggestions, suggestions, selectedSuggestionIndex, selectSuggestion, showCc, showBcc]);

  const handleSave = async () => {
    if (!formData.title.trim()) return;
    
    setSaving(true);
    try {
      const startDateTime = formData.allDay 
        ? startOfDay(parseISO(formData.startDate))
        : new Date(`${formData.startDate}T${formData.startTime}`);
      const endDateTime = formData.allDay
        ? endOfDay(parseISO(formData.endDate))
        : new Date(`${formData.endDate}T${formData.endTime}`);

      // Convert to/cc/bcc strings to attendees array for CalDAV
      const parseField = (field: string): Attendee[] => {
        if (!field.trim()) return [];
        return field.split(',').map(s => s.trim()).filter(s => s).map(part => {
          // Only treat it as "Name <email>" when angle brackets are present —
          // a bare email like user82@gmail.com must not be split at the last char.
          const angle = part.match(/<([^<>]+@[^<>]+)>/);
          if (angle) {
            const name = part.slice(0, angle.index).replace(/^['"]+|['"]+$/g, '').trim() || undefined;
            return { email: angle[1].trim(), name };
          }
          return { email: part, name: undefined };
        }).filter(a => a.email.includes('@'));
      };

      const allAttendees = [...parseField(toField), ...parseField(ccField), ...parseField(bccField)];

      await onSave({
        title: formData.title,
        description: formData.description,
        location: formData.location,
        start: startDateTime,
        end: endDateTime,
        allDay: formData.allDay,
        isVideoMeeting: formData.isVideoMeeting,
        attendees: allAttendees.length > 0 ? allAttendees : undefined,
        ...(selectedTeamId ? { team_id: selectedTeamId, is_team_event: true } : {}),
      }, {
        sendInvites: formData.sendInvites && allAttendees.length > 0,
        customMessage: formData.customMessage || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await onDelete();
    setShowDeleteConfirm(false);
  };

  const colors = event ? getEventColor(event.id) : EVENT_COLORS[0];
  const duration = getDuration();

  // State for company locations
  const [companyLocations, setCompanyLocations] = useState<Array<{ id: number; location_name: string; city?: string }>>([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  // Fetch company locations on mount
  useEffect(() => {
    const fetchCompanyLocations = async () => {
      try {
        let token: string | null = null;
        if (typeof window !== 'undefined') {
          const storedTokens = localStorage.getItem('auth_tokens');
          if (storedTokens) {
            try {
              const parsed = JSON.parse(storedTokens);
              token = parsed.access_token || null;
            } catch (e) {}
          }
        }
        if (!token) return;

        // Fetch locations from self company (is_company_self=true)
        const response = await fetch('/api/v1/contacts/locations/search?self_company=true&limit=50', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await response.json();
        if (result.success && result.data) {
          setCompanyLocations(result.data);
        }
      } catch (err) {
        console.error('Failed to fetch company locations:', err);
      }
    };
    if (isEditing) {
      fetchCompanyLocations();
    }
  }, [isEditing]);

  // Handle location quick select — 'Online' implies a video meeting (link auto-generated)
  const handleLocationSelect = (location: string) => {
    setFormData(prev => ({ ...prev, location, isVideoMeeting: location === 'Online' ? true : prev.isVideoMeeting }));
    setShowLocationDropdown(false);
  };

  const isSlideOver = presentation === 'slideover';

  return (
    <>
    <div
      className={cn(
        // z-[95] — above the app Header (z-[80/81]) and CapacitorFooter
        // (z-[89]/z-[90]); the modal must overlay the whole shell.
        'fixed inset-0 z-[95] animate-in fade-in duration-200',
        isSlideOver
          ? 'bg-black/30 backdrop-blur-sm flex justify-end'
          : 'bg-black/50 flex items-center justify-center p-4',
      )}
      onClick={isSlideOver ? onClose : undefined}
    >
      <div
        className={cn(
          'bg-white dark:bg-gray-900 shadow-2xl w-full max-w-2xl flex flex-col',
          isSlideOver
            ? 'h-full animate-in slide-in-from-right duration-300'
            : 'rounded-2xl max-h-[90vh] animate-in zoom-in-95 duration-200',
        )}
        onClick={isSlideOver ? (e) => e.stopPropagation() : undefined}
      >
        {/* Header — shared centered-icon modal chrome (same as Phone/Calendar modals) */}
        <CapacitorModalFilterHeader
          icon={CalendarIcon}
          title={isEditing ? (event ? 'Edit Event' : 'New Meeting') : event?.title || 'Event Details'}
          isDark={isDark}
          onClose={onClose}
          applyTopSafeArea={isCapacitor()}
          actions={
            !isEditing && event && !isReadonlyEvent ? (
              <>
                <button
                  type="button"
                  onClick={onEdit}
                  className={capacitorHeaderActionClass(isDark)}
                  title="Edit"
                  aria-label="Edit"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className={capacitorHeaderActionClass(isDark)}
                  title="Delete"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            ) : undefined
          }
        />

        {/* Content - scrollable area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">
          {isEditing ? (
            /* Edit Mode - Teams-inspired layout */
            <>
              {/* To/Cc/Bcc Section - matching MultiComposeManager */}
              <div className="space-y-0 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                {/* To Field */}
                <div className="relative flex items-start px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-sm text-gray-500 dark:text-gray-400 w-10 sm:w-12 pt-1 flex-shrink-0">To:</span>
                  <EmailRecipientsInput
                    value={toField}
                    onChange={setToField}
                    onFocus={() => setActiveField('to')}
                    placeholder="recipient@example.com"
                    suggestions={suggestions}
                    showSuggestions={showSuggestions && activeField === 'to'}
                    selectedSuggestionIndex={selectedSuggestionIndex}
                    onLoadSuggestions={loadSuggestions}
                    onSelectSuggestion={selectSuggestion}
                    onKeyDown={(e) => handleFieldKeyDown(e, 'to')}
                    inputRef={toInputRef}
                    className="min-w-0"
                  />
                  <div className="flex flex-wrap justify-end gap-2 text-sm items-center flex-shrink-0 pt-0.5">
                    <button
                      type="button"
                      onClick={() => setShowContactPicker(true)}
                      className={cn(
                        'shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border',
                        toField.trim()
                          ? 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                          : 'border-blue-500 bg-blue-50 dark:bg-blue-900/35 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50',
                      )}
                      title="Pick from CRM, team directory, or personal contacts"
                    >
                      <Building2 className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{toField.trim() ? 'CRM list' : 'Pick from CRM'}</span>
                      <span className="sm:hidden">CRM</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowContactPicker(true)}
                      className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                      title="Select contacts"
                    >
                      <UserPlus className="w-4 h-4" />
                    </button>
                    {!showCc && (
                      <button onClick={() => setShowCc(true)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Cc</button>
                    )}
                    {!showBcc && (
                      <button onClick={() => setShowBcc(true)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Bcc</button>
                    )}
                  </div>

                  {showSuggestions && activeField === 'to' && suggestions.length > 0 && (
                    <div
                      ref={suggestionsRef}
                      className="absolute top-full left-10 sm:left-12 right-3 sm:right-4 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
                    >
                      {suggestions.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => selectSuggestion(s)}
                          className={cn(
                            "flex items-center gap-3 w-full px-3 py-2 text-left",
                            selectedSuggestionIndex === i
                              ? "bg-blue-50 dark:bg-blue-900/30"
                              : "hover:bg-gray-50 dark:hover:bg-gray-800"
                          )}
                        >
                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                            {s.avatar_url ? (
                              <img src={s.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Users className="w-4 h-4 text-gray-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            {s.name && <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</p>}
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{s.email}</p>
                            {s.job_title && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{s.job_title}</p>}
                          </div>
                          {s.source === 'company' && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded">
                              Company
                            </span>
                          )}
                          {s.contact_type === 'candidate' && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 rounded">
                              Candidate
                            </span>
                          )}
                        </button>
                      ))}
                      <div className="px-3 py-1.5 text-xs text-gray-400 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        Use ↑↓ arrows to navigate, Enter to select
                      </div>
                    </div>
                  )}
                </div>

                {/* Cc Field */}
                {showCc && (
                  <div className="relative flex items-start px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-800">
                    <span className="text-sm text-gray-500 dark:text-gray-400 w-10 sm:w-12 pt-1 flex-shrink-0">Cc:</span>
                    <EmailRecipientsInput
                      value={ccField}
                      onChange={setCcField}
                      onFocus={() => setActiveField('cc')}
                      suggestions={suggestions}
                      showSuggestions={showSuggestions && activeField === 'cc'}
                      selectedSuggestionIndex={selectedSuggestionIndex}
                      onLoadSuggestions={loadSuggestions}
                      onSelectSuggestion={selectSuggestion}
                      onKeyDown={(e) => handleFieldKeyDown(e, 'cc')}
                      inputRef={ccInputRef}
                      className="min-w-0"
                    />
                    {showSuggestions && activeField === 'cc' && suggestions.length > 0 && (
                      <div className="absolute top-full left-10 sm:left-12 right-3 sm:right-4 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                        {suggestions.map((s, i) => (
                          <button
                            key={i}
                            onClick={() => selectSuggestion(s)}
                            className={cn(
                              "flex items-center gap-3 w-full px-3 py-2 text-left",
                              selectedSuggestionIndex === i
                                ? "bg-blue-50 dark:bg-blue-900/30"
                                : "hover:bg-gray-50 dark:hover:bg-gray-800"
                            )}
                          >
                            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                              <Users className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              {s.name && <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</p>}
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{s.email}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Bcc Field */}
                {showBcc && (
                  <div className="relative flex items-start px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-800">
                    <span className="text-sm text-gray-500 dark:text-gray-400 w-10 sm:w-12 pt-1 flex-shrink-0">Bcc:</span>
                    <EmailRecipientsInput
                      value={bccField}
                      onChange={setBccField}
                      onFocus={() => setActiveField('bcc')}
                      suggestions={suggestions}
                      showSuggestions={showSuggestions && activeField === 'bcc'}
                      selectedSuggestionIndex={selectedSuggestionIndex}
                      onLoadSuggestions={loadSuggestions}
                      onSelectSuggestion={selectSuggestion}
                      onKeyDown={(e) => handleFieldKeyDown(e, 'bcc')}
                      inputRef={bccInputRef}
                      className="min-w-0"
                    />
                    {showSuggestions && activeField === 'bcc' && suggestions.length > 0 && (
                      <div className="absolute top-full left-10 sm:left-12 right-3 sm:right-4 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                        {suggestions.map((s, i) => (
                          <button
                            key={i}
                            onClick={() => selectSuggestion(s)}
                            className={cn(
                              "flex items-center gap-3 w-full px-3 py-2 text-left",
                              selectedSuggestionIndex === i
                                ? "bg-blue-50 dark:bg-blue-900/30"
                                : "hover:bg-gray-50 dark:hover:bg-gray-800"
                            )}
                          >
                            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                              <Users className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              {s.name && <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</p>}
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{s.email}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Event Title */}
              <div>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Add title"
                  className="w-full px-4 py-3 text-lg font-medium border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white dark:focus:bg-gray-800 transition-all"
                />
              </div>

              {/* Date/Time Section — labeled rows stay clean on narrow screens */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="w-11 shrink-0 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Start
                  </span>
                  <DateTimePicker
                    value={formData.startDate ? `${formData.startDate}T${formData.startTime || '00:00'}` : ''}
                    onChange={handleStartDateTimeChange}
                    showTime={!formData.allDay}
                    isDark={isDark}
                    placeholder="Start date & time"
                    className="min-w-0 flex-1"
                  />
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className="w-4 shrink-0" aria-hidden />
                  <span className="w-11 shrink-0 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    End
                  </span>
                  <DateTimePicker
                    value={formData.endDate ? `${formData.endDate}T${formData.endTime || '00:00'}` : ''}
                    onChange={handleEndDateTimeChange}
                    showTime={!formData.allDay}
                    isDark={isDark}
                    placeholder="End date & time"
                    className="min-w-0 flex-1"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-6 sm:pl-7">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.allDay}
                      onChange={(e) => setFormData((prev) => ({ ...prev, allDay: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">All day</span>
                  </label>
                  {!formData.allDay && duration > 0 && (
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs font-medium">
                      {formatDuration(duration)}
                    </span>
                  )}
                </div>
              </div>

              {/* Location with quick select */}
              <div className="relative">
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                      onFocus={() => setShowLocationDropdown(true)}
                      placeholder="Add location"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    {showLocationDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-20 max-h-60 overflow-y-auto">
                        {/* Online option */}
                        <button
                          type="button"
                          onClick={() => handleLocationSelect('Online')}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-left border-b border-gray-100 dark:border-gray-700"
                        >
                          <Video className="w-4 h-4 text-blue-500" />
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">Online Meeting</div>
                            <div className="text-xs text-gray-500">Meeting link will be auto-generated</div>
                          </div>
                        </button>
                        
                        {/* Company locations */}
                        {companyLocations.length > 0 && (
                          <>
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50 dark:bg-gray-700/50">
                              Company Locations
                            </div>
                            {companyLocations.map(loc => (
                              <button
                                key={loc.id}
                                type="button"
                                onClick={() => handleLocationSelect(`${loc.location_name}${loc.city ? `, ${loc.city}` : ''}`)}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left"
                              >
                                <MapPin className="w-4 h-4 text-gray-400" />
                                <div>
                                  <div className="text-sm text-gray-900 dark:text-white">{loc.location_name}</div>
                                  {loc.city && <div className="text-xs text-gray-500">{loc.city}</div>}
                                </div>
                              </button>
                            ))}
                          </>
                        )}
                        
                        {/* Close dropdown on click outside */}
                        <button
                          type="button"
                          onClick={() => setShowLocationDropdown(false)}
                          className="w-full px-3 py-2 text-xs text-center text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        >
                          Close
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Type details for this new meeting"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                />
              </div>

              {/* Team selector — shown when teams are available */}
              {teams && teams.length > 0 && (
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <select
                    value={selectedTeamId || ''}
                    onChange={e => setSelectedTeamId(e.target.value || null)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">Personal event (no team)</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.icon || '👥'} {t.team_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Video meeting option - Teams style highlight box */}
              <div className={cn(
                "flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer",
                formData.isVideoMeeting 
                  ? "bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-600" 
                  : "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:border-blue-300"
              )}
              onClick={() => setFormData((prev) => ({ ...prev, isVideoMeeting: !prev.isVideoMeeting }))}
              >
                <input
                  type="checkbox"
                  checked={formData.isVideoMeeting}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isVideoMeeting: e.target.checked }))}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                  onClick={(e) => e.stopPropagation()}
                />
                <Video className={cn("w-5 h-5", formData.isVideoMeeting ? "text-blue-600" : "text-gray-400")} />
                <div className="flex-1">
                  <span className={cn(
                    "text-sm font-medium",
                    formData.isVideoMeeting ? "text-blue-700 dark:text-blue-300" : "text-gray-700 dark:text-gray-300"
                  )}>
                    Add video meeting
                  </span>
                  {formData.isVideoMeeting && (
                    <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-0.5">
                      Meeting link will be auto-generated
                    </p>
                  )}
                </div>
              </div>

              {/* Send Invitations Option - Only show when there are attendees */}
              {getAttendeesCount() > 0 && (
                <div className="space-y-3">
                  {/* Send invitations toggle */}
                  <div className={cn(
                    "flex items-center gap-3 p-3 rounded-xl border transition-all",
                    formData.sendInvites 
                      ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700" 
                      : "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700"
                  )}>
                    <input
                      type="checkbox"
                      id="sendInvites"
                      checked={formData.sendInvites}
                      onChange={(e) => setFormData((prev) => ({ ...prev, sendInvites: e.target.checked }))}
                      className="w-4 h-4 text-green-600 rounded focus:ring-green-500 border-gray-300"
                    />
                    <Mail className={cn("w-4 h-4", formData.sendInvites ? "text-green-600" : "text-gray-400")} />
                    <div className="flex-1">
                      <label htmlFor="sendInvites" className={cn(
                        "text-sm font-medium cursor-pointer",
                        formData.sendInvites ? "text-green-700 dark:text-green-300" : "text-gray-700 dark:text-gray-300"
                      )}>
                        Send email invitations
                      </label>
                      <p className={cn(
                        "text-xs mt-0.5",
                        formData.sendInvites ? "text-green-600/70 dark:text-green-400/70" : "text-gray-500"
                      )}>
                        {formData.sendInvites 
                          ? `${getAttendeesCount()} attendee${getAttendeesCount() !== 1 ? 's' : ''} will receive an email with calendar invite`
                          : 'No email will be sent to attendees'
                        }
                      </p>
                    </div>
                  </div>
                  
                  {/* Custom message for invitations */}
                  {formData.sendInvites && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                        Add a message to your invitation (optional)
                      </label>
                      <textarea
                        value={formData.customMessage}
                        onChange={(e) => setFormData((prev) => ({ ...prev, customMessage: e.target.value }))}
                        placeholder="Looking forward to meeting with you..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-sm"
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* View Mode */
            event && (
              <>
                {isReadonlyEvent && (
                  <div className="rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-800 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-200">
                    This appointment is managed in Smart Task. It appears here so your Total Connect calendar stays complete, but edits still happen in Operations.
                  </div>
                )}

                <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                  <Clock className="w-5 h-5" />
                  <div>
                    {event.allDay ? (
                      <span>All day</span>
                    ) : (
                      <>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {format(typeof event.start === 'string' ? parseISO(event.start) : new Date(event.start), 'EEEE, MMMM d, yyyy')}
                        </div>
                        <div>
                          {format(typeof event.start === 'string' ? parseISO(event.start) : new Date(event.start), 'h:mm a')} - 
                          {format(typeof event.end === 'string' ? parseISO(event.end) : new Date(event.end), 'h:mm a')}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {event.location && (
                  <div className="flex items-start gap-3 text-gray-600 dark:text-gray-400">
                    <MapPin className="w-5 h-5 mt-0.5" />
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">{event.location}</span>
                    </div>
                  </div>
                )}

                {/* Meeting URL for video calls */}
                {event.meetingUrl && (
                  <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                    <Video className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                        Video Meeting
                      </div>
                      <a 
                        href={event.meetingUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all flex items-center gap-1"
                      >
                        {event.meetingUrl}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    </div>
                  </div>
                )}

                {event.description && (
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                      {event.description}
                    </p>
                  </div>
                )}

                {event.attendees && event.attendees.length > 0 && (
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {event.attendees.length} attendee{event.attendees.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {event.attendees.map((attendee, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                            {(attendee.name || attendee.email)[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {attendee.name || attendee.email}
                            </div>
                            {attendee.name && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {attendee.email}
                              </div>
                            )}
                          </div>
                          {attendee.status && (
                            <div className={cn(
                              'px-2 py-0.5 rounded-full text-xs font-medium',
                              attendee.status === 'accepted' && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                              attendee.status === 'declined' && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                              attendee.status === 'tentative' && 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                              attendee.status === 'needsAction' && 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                            )}>
                              {attendee.status === 'needsAction' ? 'Pending' : attendee.status}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick actions */}
                {!isReadonlyEvent && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                    <button
                      onClick={() => onScheduleMeeting?.(typeof event.start === 'string' ? parseISO(event.start) : new Date(event.start))}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
                    >
                      <Video className="w-4 h-4" />
                      Start Meeting
                    </button>
                  </div>
                )}
              </>
            )
          )}
        </div>

        {/* Footer - always visible at bottom (mobile: equal-width thumb-friendly buttons) */}
        {isEditing && (
          <div
            className="flex items-center justify-end gap-3 px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}
          >
            <button
              onClick={onClose}
              className="flex-1 sm:flex-none px-5 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !formData.title.trim()}
              className="flex flex-1 sm:flex-none items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {event ? 'Save' : 'Create Event'}
            </button>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 mx-4 max-w-sm w-full shadow-xl">
              <div className="flex items-center gap-3 text-red-600 mb-4">
                <AlertCircle className="w-6 h-6" />
                <h4 className="text-lg font-semibold">Delete Event?</h4>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete &quot;{event?.title}&quot;? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    <EmailContactPickerModal
      isOpen={showContactPicker}
      onClose={() => setShowContactPicker(false)}
      onAddRecipients={mergeContactPickerRecipients}
      title="Add attendees"
      description="CRM contacts, team members, or personal contacts — multi-select adds them as invitees."
      footerIdleHint="Select one or more contacts to add as invitees."
      addButtonLabel="Add invitees"
      defaultTab="crm"
      allowedTabs={['crm', 'company', 'personal']}
    />
    </>
  );
}
